import readHouseholdField, { HOUSEHOLD_FIELDS, formatHouseholdField } from './householdFields'
import { STANDARDIZED_DATA_FIELDS } from '../standardizedDataFields'
import { resolveDataFieldType } from '../dataFields/dataFieldTypes'

/**
 * Zoeken over ALLE gegevens van een huishouden, niet alleen over de straat.
 *
 * Het huishoudenoverzicht toont tien kolommen, maar een huishouden wordt in de
 * praktijk teruggezocht op iets wat daar niet bij staat: een telefoonnummer, een
 * e-mailadres, een postcode, de woningcorporatie. Die waarden staan als
 * `PersonaData`-regels op het adres — dezelfde regels die het tabblad
 * 'Gegevens' toont.
 *
 * AI-CONTEXT: See docs/features/huishouden-zoeken-alle-gegevens.md
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 */

/** Waarmee de velden aan elkaar geplakt worden in de doorzoekbare tekst. */
const FIELD_SEPARATOR = '\n'

/**
 * Titels die het overzicht al als kolom toont. Een treffer daarin verklaart
 * zichzelf; een regel eronder die 'Achternaam · Bakker' herhaalt terwijl dat
 * twee centimeter verderop staat, is ruis.
 */
const COLUMN_TITLES = ['Straatnaam', 'Huisnummer', 'Naam', 'Voornaam', 'Achternaam']

/**
 * Vrije tekst uit het dossier. Deze velden doen WEL mee in het zoeken — je moet
 * een huishouden kunnen terugvinden op iets wat de coach opschreef — maar hun
 * inhoud verschijnt nooit in het overzicht. Een fragment uit een coachverslag
 * onder een rij in een lijst is dossierinhoud op een plek waar iedereen met
 * leestoegang meekijkt.
 */
const FREE_TEXT_TITLES = ['Bijzonderheden', 'Coachverslag', 'Notitie']

/**
 * De titels waarvan de waarde bij een treffer getoond mag worden: kort,
 * identificerend, en door het platform zelf gedefinieerd.
 *
 * Bewust een witte lijst en geen zwarte. Een organisatie mag eigen gegevens
 * toevoegen met een zelfgekozen titel ('Vereniging', maar net zo goed
 * 'Diagnose'); wat daarin staat weet alleen die organisatie. Bij een zwarte
 * lijst zou zo'n veld standaard zichtbaar zijn en pas na een incident afgedekt.
 */
const VISIBLE_VALUE_TITLES = [
    ...STANDARDIZED_DATA_FIELDS.filter((title) => !FREE_TEXT_TITLES.includes(title)),
    // Komen uit het standaard intakeformulier en staan daarom niet in de
    // platformlijst, maar zijn even onschuldig.
    'Voornaam',
    'Achternaam',
    'Telefoon',
    'Mobiel',
]

/**
 * @typedef {object} HouseholdField
 * @property {string} title - De titel van de gegevensregel
 * @property {string} value - De ingevulde waarde
 * @property {boolean} showValue - Of de waarde in het overzicht getoond mag worden
 */

/**
 * @typedef {object} HouseholdSearchEntry
 * @property {string} text - Alle waarden achter elkaar, doorzoekbaar
 * @property {Array<HouseholdField>} fields - De losse velden, op volgorde van opslag
 */

/**
 * Of deze gegevensregel meedoet in het zoeken.
 *
 * @param {object} row - Een regel uit PersonaData
 * @returns {boolean}
 */
const isSearchable = (row) => {
    if (!row?.PersonaID) return false
    if (!String(row.Value || '').trim()) return false
    // De privacyverklaring staat woordelijk hetzelfde op élk huishouden; één
    // woord eruit zou het hele overzicht als treffer opleveren.
    if (row.Type === 'privacy-consent') return false
    // Een geüpload document heeft een link als waarde. Daar zoekt niemand op,
    // en 'https' zou alles matchen.
    if (resolveDataFieldType(row) === 'upload') return false

    return true
}

/**
 * Groepeert de gegevensregels van alle huishoudens tot een zoekindex.
 *
 * Een huishouden zonder bruikbare regel komt niet in de index voor — een lege
 * ingang zou een huishouden suggereren waarover niets bekend is, terwijl het
 * verschil met 'nog niet geladen' juist telt.
 *
 * @param {Array<{PersonaID?: string, Title?: string, Value?: string, Type?: string, FieldType?: string}>} [personaDataRows]
 * @returns {Record<string, HouseholdSearchEntry>}
 */
const buildHouseholdSearchIndex = (personaDataRows) => {
    /** @type {Record<string, HouseholdSearchEntry>} */
    const index = {}

    ;(Array.isArray(personaDataRows) ? personaDataRows : []).forEach((row) => {
        if (!isSearchable(row)) return

        const title = String(row.Title || '').trim()
        const value = String(row.Value).trim()

        if (!index[row.PersonaID]) index[row.PersonaID] = { text: '', fields: [] }

        index[row.PersonaID].fields.push({
            title,
            value,
            showValue: VISIBLE_VALUE_TITLES.includes(title),
        })
    })

    Object.values(index).forEach((entry) => {
        entry.text = entry.fields.map((field) => field.value).join(FIELD_SEPARATOR)
    })

    return index
}

/**
 * Alle waarden van één huishouden als één doorzoekbare tekst.
 *
 * De velden worden met een regeleinde gescheiden en niet met een spatie: een
 * zoekterm kan geen regeleinde bevatten, dus een treffer loopt nooit over de
 * grens van twee velden heen. Zonder dat zou 'Kerkstraat' + '12' de tekst
 * 'kerkstraat12' opleveren en matcht de gebruiker op iets wat nergens staat.
 *
 * @param {Record<string, HouseholdSearchEntry>} index - Uitkomst van buildHouseholdSearchIndex
 * @param {string} addressId - De Adresses.ID van het huishouden
 * @returns {string}
 */
const householdSearchText = (index, addressId) => (index || {})[addressId]?.text || ''

/**
 * Zoekt het veld waaraan dit huishouden zijn treffer te danken heeft.
 *
 * Geeft `null` terug wanneer de treffer in een veld zit dat al als kolom op het
 * scherm staat — dan is er niets uit te leggen. Velden waarvan de waarde niet
 * getoond mag worden doen wel mee, maar leveren alleen hun naam op
 * (`showValue: false`); de aanroeper toont dan 'gevonden in Coachverslag'.
 *
 * Een veld dat zijn waarde mag tonen gaat vóór een veld dat dat niet mag: van
 * twee treffers is de zichtbare de bruikbaarste uitleg.
 *
 * @param {Record<string, HouseholdSearchEntry>} index - Uitkomst van buildHouseholdSearchIndex
 * @param {string} addressId - De Adresses.ID van het huishouden
 * @param {string} searchTerm - Wat de gebruiker intypte
 * @returns {HouseholdField | null}
 */
const findHouseholdSearchHit = (index, addressId, searchTerm) => {
    const term = String(searchTerm || '').trim().toLowerCase()
    if (!term) return null

    const fields = (index || {})[addressId]?.fields
    if (!Array.isArray(fields)) return null

    const hits = fields.filter((field) => (
        !COLUMN_TITLES.includes(field.title)
        && field.value.toLowerCase().includes(term)
    ))

    return hits.find((field) => field.showValue) || hits[0] || null
}

/**
 * De vaste gegevens van één huishouden als zoektekst, één per regel. Na de
 * migratie staan telefoon, e-mail en corporatie niet meer in de gegevens-
 * regels; zonder dit zou je een huishouden er niet meer op terugvinden. Alleen
 * wat op het huishouden zelf staat: de oude regels zitten al in de index.
 * AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md (T6)
 * @param {object|undefined} adress - Het `Adresses`-document
 * @returns {string}
 */
const fixedFieldSearchText = (adress) => {
    if (!adress) return ''
    return Object.keys(HOUSEHOLD_FIELDS)
        .map((field) => formatHouseholdField(field, readHouseholdField(adress, undefined, field)))
        .filter(Boolean)
        .join(FIELD_SEPARATOR)
}

export {
    fixedFieldSearchText,
    buildHouseholdSearchIndex,
    householdSearchText,
    findHouseholdSearchHit,
    COLUMN_TITLES,
    FREE_TEXT_TITLES,
    VISIBLE_VALUE_TITLES,
}

export default buildHouseholdSearchIndex
